import {
  parseSalarieCsv,
  parseSalarieExcel,
  salariesToCsv,
  buildSalarieExcelTemplate,
  finalizeSalarieCsvDrafts,
  findExistingSalarie,
  mergeSalarieDraft,
} from '@domain/rh/SalarieCsv';
import { uuid } from '@shared/utils/uuid';
import { libelleSalariesEnregistres } from '@shared/utils/pluriel';
import {
  linkSalarieToMatchingUser,
  normalizeEmail,
} from '@application/auth/UserSalarieLinkService';
import { matriculeDejaPris } from '@domain/paie/SalarieUniqueness';
import { fmtAnciennete, fmtFCFA, esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { LicenceLimitsService } from '@application/licence/LicenceLimitsService';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { SalarieFormView } from '@presentation/views/forms/SalarieFormView';
import { openFormModal } from '@presentation/shell/openFormModal';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { renderRowActions } from '@presentation/shell/rowActions';
import { onExclusive } from '@presentation/shell/viewDom';
import { downloadCsv, downloadBlob } from '@presentation/shell/download';
import { XLSX_LIMITS } from '@infra/import/xlsx/XlsxLimits';
import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';

type SortKey = 'matricule' | 'nom' | 'salaire' | 'anciennete';
type SortDir = 'asc' | 'desc';

function enPeriodeEssai(s: Salarie, today = new Date().toISOString().slice(0, 10)): boolean {
  return Boolean(s.essai_fin && s.essai_fin >= today);
}

function compareSalaries(a: Salarie, b: Salarie, key: SortKey, dir: SortDir): number {
  let cmp = 0;
  switch (key) {
    case 'matricule':
      cmp = a.matricule.localeCompare(b.matricule, 'fr');
      break;
    case 'nom':
      cmp = `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, 'fr');
      break;
    case 'salaire':
      cmp = (a.salaire_base || 0) - (b.salaire_base || 0);
      break;
    case 'anciennete':
      cmp = (a.date_embauche || '').localeCompare(b.date_embauche || '');
      break;
  }
  return dir === 'asc' ? cmp : -cmp;
}
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
} from '@presentation/shell/localCrud';

interface EngagementFinancierLocal {
  id: string;
  salarie_id: string;
  type: 'pret' | 'avance' | 'acompte';
  libelle: string;
  date_debut: string;
  montant_initial: number;
  mensualite: number;
  mois_preleves: number;
  statut: 'actif' | 'solde' | 'suspendu';
}

function statutBadge(statut: string): string {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    actif: { bg: '#E3F4EC', fg: '#2E8B57', label: 'Actif' },
    suspendu: { bg: '#FDF3E1', fg: '#D68910', label: 'Suspendu' },
    conge: { bg: '#E7F5F8', fg: '#00819A', label: 'Congé' },
    inactif: { bg: '#F0F2F4', fg: '#8BA8B8', label: 'Inactif' },
    sortie: { bg: '#FBE9E7', fg: '#C0392B', label: 'Sorti' },
  };
  const s = map[statut] ?? map['inactif']!;
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${s.bg};color:${s.fg};">${s.label}</span>`;
}

function rowsFor(salaries: Salarie[], withdrawnIds?: Set<string>): string {
  return salaries
    .map((s) => {
      const fonction = s.poste || s.categorie || '—';
      const contrat = s.statut_contrat || 'CDI';
      const consentBadge = withdrawnIds?.has(s.id)
        ? ` <span class="pill warning" title="Consentement retiré">CONSENTEMENT RETIRÉ</span>`
        : '';
      const actions = renderRowActions(s.id, [
        { action: 'view', label: 'Voir' },
        { action: 'finance', label: 'Suivi financier', adminOnly: true },
        { action: 'edit', label: 'Modifier', adminOnly: true },
        ...(s.statut === 'sortie' || s.statut === 'inactif'
          ? [{ action: 'reintegrer', label: 'Réintégrer', adminOnly: true }]
          : [{ action: 'delete', label: 'Sortir', variant: 'danger' as const, adminOnly: true }]),
      ]);
      return `<tr>
        <td class="mono">${esc(s.matricule)}</td>
        <td><strong>${esc(s.prenom)} ${esc(s.nom)}</strong>${consentBadge}</td>
        <td>${esc(fonction)}</td>
        <td>${esc(contrat)}</td>
        <td class="num">${esc(fmtFCFA(s.salaire_base))}</td>
        <td>${esc(fmtAnciennete(s.date_embauche))}</td>
        <td>${statutBadge(s.statut)}</td>
        <td class="num">${actions}</td>
      </tr>`;
    })
    .join('');
}

function ficheRow(label: string, value: string): string {
  return `<div class="fiche-row"><span>${esc(label)}</span><strong>${esc(value || '—')}</strong></div>`;
}

function wireFicheTabs(host: HTMLElement): void {
  const tabs = host.querySelectorAll<HTMLButtonElement>('.salarie-form-tabs .tab');
  tabs.forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabId = btn.dataset.tab;
      if (!tabId) return;
      tabs.forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      host.querySelectorAll('.tab-content').forEach((panel) => {
        panel.classList.toggle('active', panel.id === tabId);
      });
    });
  });
}

export function renderFiche(s: Salarie): string {
  const tabs = [
    { id: 'fiche-identite', label: 'Identité' },
    { id: 'fiche-contrat', label: 'Contrat' },
    { id: 'fiche-remuneration', label: 'Rémunération' },
    { id: 'fiche-famille', label: 'Famille' },
    { id: 'fiche-social', label: 'Social & fiscal' },
    { id: 'fiche-paiement', label: 'Paiement' },
    { id: 'fiche-complements', label: 'Compléments' },
  ];
  const tabsHtml = tabs
    .map(
      (t, i) =>
        `<button type="button" class="tab${i === 0 ? ' active' : ''}" data-tab="${t.id}">${t.label}</button>`,
    )
    .join('');

  return `
    <div class="fiche-salarie">
      <div class="tabs salarie-form-tabs">${tabsHtml}</div>
      <div id="fiche-identite" class="tab-content active">
        ${ficheRow('Matricule', s.matricule)}
        ${ficheRow('Nom', `${s.prenom} ${s.nom}`)}
        ${ficheRow('Date de naissance', fmtDate(s.date_naissance))}
        ${ficheRow('Sexe', s.sexe === 'M' ? 'Masculin' : s.sexe === 'F' ? 'Féminin' : '')}
        ${ficheRow('Nationalité', s.nationalite ?? '')}
        ${ficheRow('E-mail', s.email ?? '')}
        ${ficheRow('Téléphone', s.telephone ?? '')}
        ${ficheRow('Adresse', s.adresse ?? '')}
      </div>
      <div id="fiche-contrat" class="tab-content">
        ${ficheRow('Embauche', fmtDate(s.date_embauche))}
        ${ficheRow('Contrat', s.statut_contrat || 'CDI')}
        ${ficheRow('Fin de contrat', fmtDate(s.date_fin_contrat))}
        ${ficheRow('Fonction', s.poste || '')}
        ${ficheRow('Catégorie', s.categorie)}
        ${ficheRow('Centre de coût', s.centre_cout ?? '')}
        ${ficheRow('Statut', s.statut)}
      </div>
      <div id="fiche-remuneration" class="tab-content">
        ${ficheRow('Salaire de base', fmtFCFA(s.salaire_base))}
        ${ficheRow('Sursalaire', fmtFCFA(s.sursalaire ?? 0))}
      </div>
      <div id="fiche-famille" class="tab-content">
        ${ficheRow('Situation familiale', s.situation ?? s.situation_familiale ?? '')}
        ${ficheRow('Enfants à charge', String(s.enfants ?? s.nb_enfants ?? 0))}
        ${s.situation === 'veuf' || s.situation_familiale === 'veuf' ? ficheRow('Date du décès du conjoint', s.date_deces_conjoint ? fmtDate(s.date_deces_conjoint) : '') : ''}
        ${ficheRow('Conjoint avec revenus', s.conjoint_actif ? 'Oui' : 'Non')}
        ${ficheRow('Nom du conjoint', s.conjoint_nom ?? '')}
      </div>
      <div id="fiche-social" class="tab-content">
        ${ficheRow('NIF', s.nif ?? '')}
        ${ficheRow('CNSS', s.numero_cnss ?? s.cnss ?? '')}
        ${ficheRow('CNAMGS', s.cnamgs ?? '')}
        ${ficheRow('CNI / Passeport', s.cni ?? '')}
      </div>
      <div id="fiche-paiement" class="tab-content">
        ${ficheRow('Mode de paiement', s.mode_paiement ?? '')}
        ${ficheRow('Banque', s.banque ?? '')}
        ${ficheRow('IBAN / RIB', s.iban ?? '')}
      </div>
      <div id="fiche-complements" class="tab-content">
        ${ficheRow('Diplôme', s.diplome ?? '')}
        ${ficheRow('Contact urgence', s.contact_urgence_nom ?? '')}
        ${ficheRow('Téléphone urgence', s.contact_urgence_telephone ?? '')}
      </div>
    </div>
  `;
}

function typeEngagementLabel(type: EngagementFinancierLocal['type']): string {
  return type === 'pret' ? 'Prêt' : type === 'avance' ? 'Avance' : 'Acompte';
}

function resteEngagement(item: EngagementFinancierLocal): number {
  return Math.max(0, item.montant_initial - item.mensualite * item.mois_preleves);
}

export const salariesController: ViewController = async (section, ctx, shell) => {
  let all = await ctx.salaries.list();
  const tid = tenantIdOf(ctx);
  let engagements = await loadRegister<EngagementFinancierLocal>(tid, 'rh.engagements_financiers');
  const consents = await loadRegister<{
    subject_type: string;
    subject_id: string;
    withdrawn_at?: string;
    consent_date: string;
  }>(tid, 'legal.data_consents');
  const withdrawnIds = new Set(
    consents
      .filter((c) => c.subject_type === 'employee' && c.withdrawn_at)
      .map((c) => c.subject_id),
  );
  const tbody = section.querySelector<HTMLElement>('#salaries-tbody');
  const empty = section.querySelector<HTMLElement>('#salaries-empty');
  const count = section.querySelector('#count-salaries');
  const countLabel = section.querySelector('#count-salaries-label');
  const search = section.querySelector<HTMLInputElement>('#search-salaries');
  const filterActif = section.querySelector<HTMLSelectElement>('#filter-actif');
  const filterContrat = section.querySelector<HTMLSelectElement>('#filter-statut');
  const filterEssaiActif = section.querySelector<HTMLInputElement>('#filter-essai-actif');
  const tableHead = section.querySelector<HTMLElement>('#salaries-table thead');
  const canMutate = canMutateRecords(shell.permission);
  let sortKey: SortKey = 'matricule';
  let sortDir: SortDir = 'asc';

  const paintSortIndicators = (): void => {
    tableHead?.querySelectorAll<HTMLElement>('th[data-sort]').forEach((th) => {
      const key = th.dataset.sort as SortKey;
      const base = th.textContent?.replace(/ [▲▼]$/, '') ?? '';
      th.textContent = key === sortKey ? `${base} ${sortDir === 'asc' ? '▲' : '▼'}` : base;
    });
  };

  const apply = (): void => {
    const q = (search?.value ?? '').toLowerCase().trim();
    const mode = filterActif?.value ?? 'actif';
    const contrat = filterContrat?.value ?? '';
    const essaiSeulement = Boolean(filterEssaiActif?.checked);
    let list = all;
    if (mode === 'actif')
      list = list.filter(
        (s) => s.statut === 'actif' || s.statut === 'suspendu' || s.statut === 'conge',
      );
    else if (mode === 'sorti')
      list = list.filter((s) => s.statut === 'sortie' || s.statut === 'inactif');
    if (contrat) list = list.filter((s) => (s.statut_contrat || 'CDI') === contrat);
    if (essaiSeulement) list = list.filter((s) => enPeriodeEssai(s));
    if (q) {
      list = list.filter((s) =>
        `${s.prenom} ${s.nom} ${s.matricule} ${s.poste ?? ''} ${s.categorie}`
          .toLowerCase()
          .includes(q),
      );
    }
    list = [...list].sort((a, b) => compareSalaries(a, b, sortKey, sortDir));
    if (tbody) tbody.innerHTML = rowsFor(list, withdrawnIds);
    if (empty) empty.style.display = list.length === 0 ? '' : 'none';
    if (count) count.textContent = String(list.length);
    if (countLabel) countLabel.textContent = libelleSalariesEnregistres(list.length);
    paintSortIndicators();
  };

  apply();
  onExclusive(search, 'input', apply);
  onExclusive(filterActif, 'change', apply);
  onExclusive(filterContrat, 'change', apply);
  onExclusive(filterEssaiActif, 'change', apply);
  onExclusive(tableHead, 'click', (e) => {
    const th = (e.target as HTMLElement).closest<HTMLElement>('th[data-sort]');
    if (!th?.dataset.sort) return;
    const key = th.dataset.sort as SortKey;
    if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    else {
      sortKey = key;
      sortDir = 'asc';
    }
    apply();
  });

  const refresh = async (): Promise<void> => {
    all = await ctx.salaries.list();
    engagements = await loadRegister<EngagementFinancierLocal>(tid, 'rh.engagements_financiers');
    apply();
  };

  const openEngagements = (s: Salarie): void => {
    const renderPanel = (host: HTMLElement): void => {
      const rows = engagements
        .filter((item) => item.salarie_id === s.id)
        .sort((a, b) => b.date_debut.localeCompare(a.date_debut));

      host.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:12px;">
          <div style="font-size:13px;color:#586069;">Suivi RH des avances, acomptes et prêts de ${esc(s.prenom)} ${esc(s.nom)}</div>
          ${canMutate ? '<button type="button" class="btn btn-primary btn-sm" data-add-engagement>+ Ajouter</button>' : ''}
        </div>
        <table class="data">
          <thead><tr><th>Type</th><th>Libellé</th><th>Date début</th><th class="num">Montant initial</th><th class="num">Mensualité</th><th class="num">Mois prélevés</th><th class="num">Reste</th><th>Statut</th><th class="num">Actions</th></tr></thead>
          <tbody id="engagements-tbody">${
            rows.length === 0
              ? emptyRow(9, 'Aucun engagement enregistré.')
              : rows
                  .map(
                    (item) => `<tr>
                <td>${esc(typeEngagementLabel(item.type))}</td>
                <td>${esc(item.libelle)}</td>
                <td>${esc(fmtDate(item.date_debut))}</td>
                <td class="num">${esc(fmtFCFA(item.montant_initial))}</td>
                <td class="num">${esc(fmtFCFA(item.mensualite))}</td>
                <td class="num">${item.mois_preleves}</td>
                <td class="num">${esc(fmtFCFA(resteEngagement(item)))}</td>
                <td>${esc(item.statut)}</td>
                <td class="num">${crudActions(item.id)}</td>
              </tr>`,
                  )
                  .join('')
          }</tbody>
        </table>
        <div style="margin-top:10px;font-size:12px;color:#586069;">Le reste à payer est calculé automatiquement : montant initial - (mensualité × mois prélevés).</div>
      `;

      const openEditor = (existing?: EngagementFinancierLocal): void => {
        if (!canMutate) {
          ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
          return;
        }
        openRegisterForm({
          parent: section,
          title: existing ? 'Modifier un engagement' : 'Nouvel engagement',
          fields: [
            {
              kind: 'select',
              name: 'type',
              label: 'Type',
              options: [
                { value: 'pret', label: 'Prêt' },
                { value: 'avance', label: 'Avance' },
                { value: 'acompte', label: 'Acompte' },
              ],
            },
            {
              kind: 'text',
              name: 'libelle',
              label: 'Libellé',
              required: true,
              placeholder: 'Ex. prêt personnel',
            },
            { kind: 'date', name: 'date_debut', label: 'Date début', required: true },
            { kind: 'number', name: 'montant_initial', label: 'Montant initial', min: 0 },
            { kind: 'number', name: 'mensualite', label: 'Mensualité', min: 0 },
            { kind: 'number', name: 'mois_preleves', label: 'Mois déjà prélevés', min: 0 },
            {
              kind: 'select',
              name: 'statut',
              label: 'Statut',
              options: [
                { value: 'actif', label: 'Actif' },
                { value: 'solde', label: 'Soldé' },
                { value: 'suspendu', label: 'Suspendu' },
              ],
            },
          ],
          values: existing,
          onSubmit: async (data) => {
            const item: EngagementFinancierLocal = {
              id: existing?.id ?? `eng_${Date.now().toString(36)}`,
              salarie_id: s.id,
              type: String(data.type) as EngagementFinancierLocal['type'],
              libelle: String(data.libelle),
              date_debut: String(data.date_debut),
              montant_initial: Number(data.montant_initial ?? 0),
              mensualite: Number(data.mensualite ?? 0),
              mois_preleves: Number(data.mois_preleves ?? 0),
              statut: String(data.statut) as EngagementFinancierLocal['statut'],
            };
            engagements = upsertById(engagements, item);
            await saveRegister(tid, 'rh.engagements_financiers', engagements);
            renderPanel(host);
          },
        });
      };

      host.querySelector('[data-add-engagement]')?.addEventListener('click', () => openEditor());
      bindCrudTable(
        host.querySelector('#engagements-tbody'),
        section,
        (id) => openEditor(engagements.find((item) => item.id === id)),
        async (id) => {
          engagements = removeById(engagements, id);
          await saveRegister(tid, 'rh.engagements_financiers', engagements);
          renderPanel(host);
        },
        canMutate,
      );
    };

    openFormModal(
      section,
      { title: `Suivi financier — ${s.prenom} ${s.nom}`, size: 'lg' },
      (host) => {
        renderPanel(host);
      },
    );
  };

  const openEditor = (existing?: Salarie): void => {
    if (!existing) {
      const check = LicenceLimitsService.canAddSalarie(ctx.getLicenceRuntime(), all.length);
      if (!check.allowed) {
        ToastService.warning(check.message ?? 'Limite de salariés atteinte');
        return;
      }
    }
    openFormModal(
      section,
      { title: existing ? 'Modifier le salarié' : 'Nouveau salarié', size: 'lg' },
      (host, close) => {
        SalarieFormView.render(
          host,
          async () => {
            close();
            await refresh();
          },
          close,
          existing,
          all,
        );
      },
    );
  };

  onExclusive(tbody, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn) return;
    const s = all.find((x) => x.id === btn.dataset.id);
    if (!s) return;
    const action = btn.dataset.action;
    if (action === 'view') {
      openFormModal(section, { title: `${s.prenom} ${s.nom}`, size: 'lg' }, (host, close) => {
        host.innerHTML = renderFiche(s);
        wireFicheTabs(host);
        const footer = document.createElement('div');
        footer.slot = 'footer';
        const ok = document.createElement('button');
        ok.type = 'button';
        ok.className = 'btn btn-primary';
        ok.textContent = 'Fermer';
        ok.addEventListener('click', close);
        footer.appendChild(ok);
        host.parentElement?.appendChild(footer);
      });
      return;
    }
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    if (action === 'finance') {
      openEngagements(s);
      return;
    }
    if (action === 'edit') openEditor(s);
    if (action === 'reintegrer') {
      void (async () => {
        openFormModal(
          section,
          { title: `Réintégration — ${s.prenom} ${s.nom}`, size: 'md' },
          (host, close) => {
            const today = new Date().toISOString().slice(0, 10);
            host.innerHTML = `
              <p style="margin:0 0 12px;font-size:13px;line-height:1.5;color:#586069;">
                Le salarié repasse en statut <strong>Actif</strong> (ex. retour de stage, CDD, MAD).
              </p>
              <form id="reinteg-form">
                <div class="field">
                  <label for="reinteg-date">Date de réintégration *</label>
                  <input id="reinteg-date" name="date_reintegration" type="date" required value="${esc(today)}">
                </div>
                <div style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end;">
                  <button type="button" class="btn btn-secondary" data-cancel>Annuler</button>
                  <button type="submit" class="btn btn-primary">Confirmer la réintégration</button>
                </div>
              </form>`;
            host.querySelector('[data-cancel]')?.addEventListener('click', close);
            host.querySelector('#reinteg-form')?.addEventListener('submit', (ev) => {
              ev.preventDefault();
              const date =
                host.querySelector<HTMLInputElement>('#reinteg-date')?.value.trim() || today;
              void (async () => {
                await ctx.salaries.update({
                  ...s,
                  statut: 'actif',
                  date_reintegration: date,
                });
                await recordAuditEvent(ctx, {
                  action: 'update',
                  entity: 'salarie',
                  entity_id: s.id,
                  description: `Réintégration de ${s.prenom} ${s.nom} au ${date}`,
                });
                ToastService.success(`Réintégration enregistrée — ${s.prenom} ${s.nom}`);
                close();
                await refresh();
              })();
            });
          },
        );
      })();
      return;
    }
    if (action === 'delete') {
      void (async () => {
        const ok = await confirmAction({
          host: section,
          title: 'Sortie du salarié',
          message: `${s.prenom} ${s.nom} passera en statut sorti. Les bulletins déjà émis sont conservés.`,
          confirmLabel: 'Enregistrer la sortie',
          danger: true,
        });
        if (!ok) return;
        await ctx.salaries.update({
          ...s,
          statut: 'sortie',
          date_sortie: new Date().toISOString().slice(0, 10),
        });
        await recordAuditEvent(ctx, {
          action: 'update',
          entity: 'salarie',
          entity_id: s.id,
          description: `Sortie de ${s.prenom} ${s.nom}`,
        });
        const effacer = await confirmAction({
          host: section,
          title: 'Droit à l’effacement (RGPD)',
          message:
            'Souhaitez-vous aussi anonymiser les données personnelles (nom, e-mail, téléphone…) ? Les bulletins restent pour conservation légale.',
          confirmLabel: 'Anonymiser',
          danger: true,
        });
        if (effacer) {
          const { anonymiserSalarie } = await import('@domain/rgpd/AnonymisationSalarie');
          const fresh = await ctx.salaries.get(s.id);
          if (fresh) {
            await ctx.salaries.update(anonymiserSalarie(fresh));
            await recordAuditEvent(ctx, {
              action: 'delete',
              entity: 'salarie',
              entity_id: s.id,
              description: `Effacement RGPD (anonymisation) de ${s.matricule}`,
            });
            ToastService.success('Données personnelles anonymisées');
          }
        } else {
          ToastService.success(`${s.prenom} ${s.nom} est sorti des effectifs`);
        }
        await refresh();
      })();
    }
  });

  const w = window as unknown as Record<string, unknown>;
  w['openSalarieModal'] = () => {
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    openEditor();
  };
  w['openImportSalaries'] = (event?: Event) => {
    if (!canMutate) {
      ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    const input = (event?.target as HTMLInputElement | null) ?? null;
    const file = input?.files?.[0];
    if (!file) {
      section.querySelector<HTMLInputElement>('#salaries-import-input')?.click();
      return;
    }
    const name = file.name.toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    void (async () => {
      let rawDrafts: ReturnType<typeof parseSalarieCsv>['drafts'] = [];
      let errors: { ligne: number; message: string }[] = [];
      try {
        if (isExcel) {
          if (file.size > XLSX_LIMITS.maxFileBytes) {
            throw new Error(
              `fichier trop volumineux (maximum ${Math.round(XLSX_LIMITS.maxFileBytes / (1024 * 1024))} Mo)`,
            );
          }
          const buffer = await file.arrayBuffer();
          const parsed = parseSalarieExcel(buffer);
          rawDrafts = parsed.drafts;
          errors = [...parsed.errors];
        } else {
          const texte = await file.text();
          const parsed = parseSalarieCsv(texte);
          rawDrafts = parsed.drafts;
          errors = [...parsed.errors];
        }
      } catch (err) {
        ToastService.error(
          `Lecture du fichier impossible : ${err instanceof Error ? err.message : String(err)}`,
        );
        if (input) input.value = '';
        return;
      }
      if (input) input.value = '';
      if (rawDrafts.length === 0 && errors.length === 0) {
        ToastService.warning('Fichier vide ou sans ligne de données');
        return;
      }
      const current = await ctx.salaries.list();
      const drafts = finalizeSalarieCsvDrafts(rawDrafts, current);
      let created = 0;
      let updated = 0;
      const byMatricule = new Map(current.map((s) => [s.matricule.trim().toLowerCase(), s]));
      const byCnss = new Map(
        current
          .filter((s) => (s.numero_cnss ?? s.cnss ?? '').trim())
          .map((s) => [(s.numero_cnss ?? s.cnss ?? '').trim().toLowerCase(), s]),
      );
      const byEmail = new Map(
        current
          .filter((s) => (s.email ?? '').trim())
          .map((s) => [(s.email ?? '').trim().toLowerCase(), s]),
      );
      const detailLignes: Array<{ ligne: string; statut: string; detail: string }> = [];

      for (const draft of drafts) {
        const ligneLabel = String(draft.ligne ?? '—');
        const existing = findExistingSalarie(draft, byMatricule, byCnss, byEmail);
        if (existing) {
          const salarie = mergeSalarieDraft(existing, draft, normalizeEmail(draft.email));
          await ctx.salaries.update(salarie);
          await linkSalarieToMatchingUser(ctx, salarie);
          byMatricule.set(salarie.matricule.trim().toLowerCase(), salarie);
          if (salarie.numero_cnss ?? salarie.cnss) {
            byCnss.set((salarie.numero_cnss ?? salarie.cnss ?? '').trim().toLowerCase(), salarie);
          }
          if (salarie.email) byEmail.set(salarie.email.trim().toLowerCase(), salarie);
          updated += 1;
          detailLignes.push({
            ligne: ligneLabel,
            statut: 'Mis à jour',
            detail: `${salarie.matricule} — ${salarie.prenom} ${salarie.nom}`,
          });
          continue;
        }
        const check = LicenceLimitsService.canAddSalarie(ctx.getLicenceRuntime(), byMatricule.size);
        if (!check.allowed) {
          errors.push({
            ligne: draft.ligne ?? 0,
            message: check.message ?? 'Limite de salariés atteinte',
          });
          detailLignes.push({
            ligne: ligneLabel,
            statut: 'Erreur',
            detail: check.message ?? 'Limite de salariés atteinte',
          });
          break;
        }
        if (matriculeDejaPris([...byMatricule.values()], draft.matricule)) {
          errors.push({
            ligne: draft.ligne ?? 0,
            message: `Matricule déjà utilisé : ${draft.matricule}`,
          });
          detailLignes.push({
            ligne: ligneLabel,
            statut: 'Erreur',
            detail: `Matricule déjà utilisé : ${draft.matricule}`,
          });
          continue;
        }
        const salarie = {
          ...draft,
          email: normalizeEmail(draft.email),
          numero_cnss: draft.cnss,
          id: uuid('sal'),
          tenant_id: ctx.tenantContext.tenantId(),
          cree_le: new Date().toISOString(),
        } as Salarie;
        await ctx.salaries.add(salarie);
        await linkSalarieToMatchingUser(ctx, salarie);
        byMatricule.set(salarie.matricule.trim().toLowerCase(), salarie);
        if (salarie.numero_cnss ?? salarie.cnss) {
          byCnss.set((salarie.numero_cnss ?? salarie.cnss ?? '').trim().toLowerCase(), salarie);
        }
        if (salarie.email) byEmail.set(salarie.email.trim().toLowerCase(), salarie);
        created += 1;
        detailLignes.push({
          ligne: ligneLabel,
          statut: 'Créé',
          detail: `${salarie.matricule} — ${salarie.prenom} ${salarie.nom}`,
        });
      }

      for (const err of errors) {
        if (detailLignes.some((d) => d.ligne === String(err.ligne) && d.statut === 'Erreur')) {
          continue;
        }
        detailLignes.push({
          ligne: String(err.ligne || '—'),
          statut: 'Erreur',
          detail: err.message,
        });
      }

      await recordAuditEvent(ctx, {
        action: 'create',
        entity: 'salarie',
        description: `Import salariés : ${created} créé(s), ${updated} mis à jour, ${errors.length} erreur(s)`,
      });

      const recap = `${created} créé(s), ${updated} mis à jour, ${errors.length} erreur(s)`;
      openFormModal(section, { title: 'Compte-rendu d’import salariés', size: 'lg' }, (host, close) => {
        host.innerHTML = `
          <p style="margin:0 0 12px;line-height:1.5;">${esc(recap)}</p>
          <div style="max-height:360px;overflow:auto;border:1px solid #DAE0E4;border-radius:8px;">
            <table class="data" style="margin:0;">
              <thead><tr><th>Ligne</th><th>Statut</th><th>Détail</th></tr></thead>
              <tbody>
                ${detailLignes
                  .map(
                    (r) =>
                      `<tr><td>${esc(r.ligne)}</td><td>${esc(r.statut)}</td><td>${esc(r.detail)}</td></tr>`,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
          <div style="margin-top:14px;display:flex;gap:8px;justify-content:flex-end;">
            <button type="button" class="btn btn-primary" id="import-sal-ok">Fermer</button>
          </div>`;
        host.querySelector('#import-sal-ok')?.addEventListener('click', () => close());
      });

      if (created + updated === 0) {
        ToastService.error(errors[0]?.message ?? 'Aucune ligne importable');
      } else {
        ToastService.success(`Import : ${recap}`);
      }
      await refresh();
    })();
  };
  w['exportSalariesCSV'] = () => {
    downloadCsv(salariesToCsv(all), 'salaries.csv');
    ToastService.success('Export CSV généré (ouvrable dans Excel)');
  };
  w['downloadSalarieCsvTemplate'] = () => {
    void (async () => {
      try {
        const buffer = await buildSalarieExcelTemplate();
        downloadBlob(
          new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
          'modele_salaries.xlsx',
        );
        ToastService.success(
          'Modèle Excel multi-onglets téléchargé — Identité, Contrat, Famille, Médecine, Paiement, Urgence, Sortie',
        );
      } catch (err) {
        ToastService.error(
          `Génération du modèle impossible : ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    })();
  };
};
